import React, { useEffect, useState, memo, useRef } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { Button, Dropdown, Toggle, Loading, Input } from '../../../../common/ui';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../../../common/actions';
import {
  findBusinessDaySettings,
  updateBusinessDaySettings,
} from '../actions';
import { setBranchId } from '../../../auth/actions';
import Axios from '../../../../common/util/Axios';

const EMPTY_FORM = {
  'is-business-day-enabled': '',
  'business-day-master-register-cashier-id': '',
  'business-day-variance-threshold': '',
};

const BusinessDaySettings = memo(
  ({
    auth: { branchList, branchId },
    businessDaySettingsState: { businessDaySettingsData },
    showAlert,
    findBusinessDaySettings,
    setBranchId,
    updateBusinessDaySettings,
  }) => {
    const originalDataRef = useRef({ ...EMPTY_FORM });
    const findSeqRef = useRef(0);
    const [businessDaySettingsForm, setBusinessDaySettingsForm] = useState({
      ...EMPTY_FORM,
    });
    const [cashiers, setCashiers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      if (!branchId) {
        return;
      }
      const seq = ++findSeqRef.current;
      const load = async () => {
        const ok = await findBusinessDaySettings(branchId);
        if (seq !== findSeqRef.current) {
          return;
        }
        if (!ok) {
          setSettingsLoadFailed(true);
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage:
              'Failed to load Business Day settings. Showing last known values — do not save until reload succeeds.',
          });
        } else {
          setSettingsLoadFailed(false);
        }
      };
      load();
    }, [branchId]);

    useEffect(() => {
      if (!branchId) {
        setCashiers([]);
        return;
      }
      const loadCashiers = async () => {
        try {
          const res = await Axios.get('/api/dashboard/cashiers/find-all-by', {
            params: { branchId },
          });
          setCashiers((res.data && res.data.data) || []);
        } catch (err) {
          console.error('Failed to load Registers for Business Day settings', err);
          setCashiers([]);
          showAlert({
            isAlertOpen: true,
            alertType: 'danger',
            alertMessage:
              'Failed to load Registers for Master Register. Try again or refresh the page.',
          });
        }
      };
      loadCashiers();
    }, [branchId]);

    useEffect(() => {
      const nextForm = { ...EMPTY_FORM };
      if (Array.isArray(businessDaySettingsData)) {
        for (const item of businessDaySettingsData) {
          const paramKey = item?.param;
          const paramValue = item?.value;
          if (
            paramKey &&
            Object.prototype.hasOwnProperty.call(nextForm, paramKey)
          ) {
            nextForm[paramKey] = paramValue ?? '';
          }
        }
      }
      originalDataRef.current = { ...nextForm };
      setBusinessDaySettingsForm(nextForm);
    }, [businessDaySettingsData]);

    const handleChangeBranch = (value) => {
      setBranchId(value);
    };

    const handleToggleChange = (e) => {
      const { name, checked } = e.target;
      setBusinessDaySettingsForm((prev) => ({
        ...prev,
        [name]: checked ? 'true' : 'false',
      }));
    };

    const handleMasterRegisterChange = (value) => {
      setBusinessDaySettingsForm((prev) => ({
        ...prev,
        'business-day-master-register-cashier-id': value == null ? '' : String(value),
      }));
    };

    const handleVarianceChange = (value) => {
      setBusinessDaySettingsForm((prev) => ({
        ...prev,
        'business-day-variance-threshold': value == null ? '' : String(value),
      }));
    };

    const onSubmit = async (e) => {
      e.preventDefault();
      if (!branchId) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage: 'Select a branch before saving Business Day settings.',
        });
        return;
      }
      if (settingsLoadFailed) {
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage:
            'Business Day settings did not load successfully. Reload before saving.',
        });
        return;
      }

      setIsLoading(true);
      const changedFields = {};
      Object.keys(businessDaySettingsForm).forEach((key) => {
        const currentVal = businessDaySettingsForm[key] ?? '';
        const originalVal = originalDataRef.current[key] ?? '';
        if (currentVal !== originalVal) {
          changedFields[key] = currentVal;
        }
      });

      if (Object.keys(changedFields).length === 0) {
        showAlert({
          isAlertOpen: true,
          alertType: 'success',
          alertMessage: 'No changes to save!',
        });
        setIsLoading(false);
        return;
      }

      const result = await updateBusinessDaySettings({
        businessDaySettingsData: changedFields,
        branchId,
      });

      if (result && result.success) {
        // Treat saved form as last-known good so a failed reload cannot wipe UI.
        originalDataRef.current = { ...businessDaySettingsForm };
        const reloaded = await findBusinessDaySettings(branchId);
        if (reloaded) {
          setSettingsLoadFailed(false);
          showAlert({
            isAlertOpen: true,
            alertType: 'success',
            alertMessage: 'Business Day settings updated successfully!',
          });
        } else {
          setSettingsLoadFailed(true);
          showAlert({
            isAlertOpen: true,
            alertType: 'warning',
            alertMessage:
              'Settings saved, but reload failed. Values on screen are what you saved — refresh before editing further.',
          });
        }
      } else {
        // Restore form from last known server values when disable is blocked.
        setBusinessDaySettingsForm({ ...originalDataRef.current });
        showAlert({
          isAlertOpen: true,
          alertType: 'danger',
          alertMessage:
            (result && result.message) ||
            'Failed to update Business Day settings!',
        });
      }
      setIsLoading(false);
    };

    const onClickCancel = () => {
      navigate('/');
    };

    const branchOptions = (branchList || []).map((b) => ({
      value: b.id,
      label: b.name,
    }));

    const masterRegisterOptions = [
      { value: '', label: 'Not set' },
      ...(cashiers || []).map((c) => ({
        value: String(c.id),
        label: c.name
          ? `${c.name}${c.cashierNo ? ` (${c.cashierNo})` : ''}`
          : c.cashierNo || String(c.id),
      })),
    ];

    return (
      <div className={styles.container}>
        <header className={styles.pageHeader}>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>Business Day Settings</h1>
            <p className={styles.breadcrumb}>Settings / Business Day</p>
          </div>
          {branchList && branchList.length > 0 && (
            <div className={styles.branchSelectContainer}>
              <Dropdown
                id="business-day-branch-select"
                label="Branch"
                value={branchId}
                onChange={handleChangeBranch}
                options={branchOptions}
                placeholder="Select branch"
                disabled={isLoading}
              />
            </div>
          )}
        </header>
        <div className={styles.body}>
          <div className={styles.formCard}>
            {isLoading && <Loading variant="overlay" label="" />}
            <form onSubmit={onSubmit} noValidate>
              <div className={styles.topDiv}>
                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Business Day</h2>
                  <p className={styles.topicCaption}>
                    Enable Business Day trading for this branch. Sibling branches
                    are unchanged.
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Toggle
                    checked={
                      businessDaySettingsForm['is-business-day-enabled'] ===
                      'true'
                    }
                    onChange={handleToggleChange}
                    name="is-business-day-enabled"
                    label="Enable Business Day"
                    labelPlacement="start"
                  />
                </div>

                <div className={styles.line} />

                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Master Register</h2>
                  <p className={styles.topicCaption}>
                    In Legacy Mode, Start New requires a Master Register. Leave
                    empty to block Start New until configured.
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Dropdown
                    id="business-day-master-register"
                    label="Master Register"
                    value={
                      businessDaySettingsForm[
                        'business-day-master-register-cashier-id'
                      ] || ''
                    }
                    onChange={handleMasterRegisterChange}
                    options={masterRegisterOptions}
                    placeholder="Select Master Register"
                  />
                </div>

                <div className={styles.line} />

                <div className={styles.topicDiv}>
                  <h2 className={styles.topicLabel}>Cash variance threshold</h2>
                  <p className={styles.topicCaption}>
                    Used at Register Day End. Clear to use reason-only variance
                    (no threshold-forced PIN).
                  </p>
                </div>
                <div className={styles.rowDiv}>
                  <Input
                    id="business-day-variance-threshold"
                    name="business-day-variance-threshold"
                    label="Variance threshold"
                    type="number"
                    value={
                      businessDaySettingsForm[
                        'business-day-variance-threshold'
                      ] || ''
                    }
                    onChange={handleVarianceChange}
                    placeholder="Leave empty if unset"
                  />
                </div>
              </div>

              <div className={styles.buttonPanelContainer}>
                <Button
                  type="submit"
                  disabled={isLoading || !branchId || settingsLoadFailed}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClickCancel}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state) => ({
  auth: state.authState,
  businessDaySettingsState: state.businessDaySettingsState,
});

export default connect(mapStateToProps, {
  showAlert,
  findBusinessDaySettings,
  setBranchId,
  updateBusinessDaySettings,
})(BusinessDaySettings);
